import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import OpenSeadragon from 'openseadragon';
import './CoregModal.css';
import { AuthContext } from '../../context/authContext';
import ajaxActions from '../../services/ajaxActions';
import {
  applyTwoPointRegistration,
  applyTiledImageTransform,
  applyTiledImageState,
  buildFiducialsPayload,
  buildCoregSavePayload,
  captureTiledImageState,
  computeDermToMacroMatrix,
  COREG_META_KEY,
  findCoregMovingLayer,
  formatFiducialPoint,
  formatMatrixCell,
  getRegistrationState,
  isCompleteFiducials,
  layoutUnregisteredOverlay,
  nudgeMovingLayer,
  updateRegistrationDataForDerm,
} from '../../utils/coregRegistration';
import {
  formatCoregImageLabel,
  getCoregEligibleImages,
  getCoregImageKey,
} from '../../utils/registrationHelpers';
import {
  getCoregFolderKey,
  loadCoregFromLocalStorage,
  saveCoregToLocalStorage,
} from '../../utils/coregLocalStorage';
import { createDZITileSource, getImageItemId } from '../../utils/imageHelpers';
import { resetTiledImageLayer } from '../../utils/openseadragonHelpers';

const TWO_POINT_STEPS = [
  { key: 'moveA', label: 'Move A', layer: 'moving', color: 'move' },
  { key: 'ontoA', label: 'Onto A', layer: 'static', color: 'onto' },
  { key: 'moveB', label: 'Move B', layer: 'moving', color: 'move' },
  { key: 'ontoB', label: 'Onto B', layer: 'static', color: 'onto' },
];

const SIDE_BY_SIDE_GAP = 0.08;

function fiducialsMatchPair(fiducials, staticItemId, movingItemId) {
  if (!fiducials?.movingItemId || !fiducials?.staticItemId) return false;
  return (
    fiducials.movingItemId === movingItemId
    && fiducials.staticItemId === staticItemId
  );
}

function getSavedCoregState(selectedFolder) {
  const folderKey = getCoregFolderKey(selectedFolder);
  return selectedFolder?.meta?.[COREG_META_KEY]
    ?? loadCoregFromLocalStorage(folderKey)?.[COREG_META_KEY]
    ?? null;
}

function setFiducialPlacementGestures(viewer, active) {
  if (!viewer) return;

  const clickToZoom = !active;
  viewer.gestureSettingsMouse.clickToZoom = clickToZoom;
  viewer.gestureSettingsMouse.dblClickToZoom = clickToZoom;
  viewer.gestureSettingsMouse.scrollToZoom = true;
  viewer.gestureSettingsMouse.dragToPan = true;

  viewer.gestureSettingsTouch.clickToZoom = clickToZoom;
  viewer.gestureSettingsTouch.dblClickToZoom = clickToZoom;
  viewer.gestureSettingsTouch.pinchToZoom = true;
  viewer.gestureSettingsTouch.dragToPan = true;
}

/** Place the moving (derm) image to the right of the reference (macro) image. */
function layoutSideBySide(staticTiledImage, movingTiledImage) {
  if (!staticTiledImage || !movingTiledImage) return;

  const staticBounds = staticTiledImage.getBoundsNoRotate(false);
  movingTiledImage.setRotation(0, false);
  movingTiledImage.setFlip(false);
  movingTiledImage.setWidth(staticBounds.width, false);
  movingTiledImage.setPosition(
    new OpenSeadragon.Point(staticBounds.x + staticBounds.width + SIDE_BY_SIDE_GAP, staticBounds.y),
    false
  );
}

/** Stack the moving image on the reference using a saved overlay transform. */
function applyOverlayLayout(staticTiledImage, movingTiledImage, overlayState) {
  if (!staticTiledImage || !movingTiledImage) return;

  if (overlayState) {
    applyTiledImageTransform(movingTiledImage, overlayState);
  } else {
    layoutUnregisteredOverlay(staticTiledImage, movingTiledImage);
  }
}

function CoregModal({
  isOpen,
  onClose,
  selectedFolder,
  macroImage,
  dermImage,
  onRegistrationUpdated,
}) {
  const { isLoggedIn } = useContext(AuthContext);
  const viewerContainerRef = useRef(null);
  const viewerRef = useRef(null);
  const tiledImagesRef = useRef({ static: null, moving: null });
  const tiledImageMetaRef = useRef(new Map());
  const pinOverlaysRef = useRef([]);
  const twoPointRef = useRef({ active: false, stepIndex: 0, points: {} });
  const overlayStateRef = useRef(null);
  const fiducialsRef = useRef(null);
  const layoutModeRef = useRef('separate');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [twoPointActive, setTwoPointActive] = useState(false);
  const [twoPointStepIndex, setTwoPointStepIndex] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [layoutMode, setLayoutMode] = useState('separate');
  const [dermOpacity, setDermOpacity] = useState(100);
  const [savedFiducials, setSavedFiducials] = useState(null);
  const [transformMatrix, setTransformMatrix] = useState(null);
  const [activeMacro, setActiveMacro] = useState(macroImage);
  const [activeDerm, setActiveDerm] = useState(dermImage);
  const [swappingLayer, setSwappingLayer] = useState(false);

  const activeMacroRef = useRef(macroImage);
  const activeDermRef = useRef(dermImage);

  useEffect(() => {
    activeMacroRef.current = activeMacro;
  }, [activeMacro]);

  useEffect(() => {
    activeDermRef.current = activeDerm;
  }, [activeDerm]);

  const coregImageOptions = useMemo(
    () => getCoregEligibleImages(selectedFolder?.meta?.registrationData),
    [selectedFolder?.meta?.registrationData]
  );

  useEffect(() => {
    layoutModeRef.current = layoutMode;
  }, [layoutMode]);

  const setMovingOpacity = useCallback((percent) => {
    const clamped = Math.min(100, Math.max(0, percent));
    setDermOpacity(clamped);
    tiledImagesRef.current.moving?.setOpacity(clamped / 100);
  }, []);

  const refreshTransformDisplay = useCallback(() => {
    const moving = tiledImagesRef.current.moving;
    const staticImage = tiledImagesRef.current.static;
    if (!moving || !staticImage || !overlayStateRef.current) {
      setTransformMatrix(null);
      return;
    }
    setTransformMatrix(computeDermToMacroMatrix(moving, staticImage));
  }, []);

  const persistOverlayState = useCallback(() => {
    const moving = tiledImagesRef.current.moving;
    if (moving) {
      overlayStateRef.current = captureTiledImageState(moving);
    }
    refreshTransformDisplay();
  }, [refreshTransformDisplay]);

  const applySavedFiducials = useCallback((fiducials) => {
    if (!fiducials?.points) {
      fiducialsRef.current = null;
      setSavedFiducials(null);
      return;
    }
    fiducialsRef.current = fiducials;
    setSavedFiducials(fiducials);
  }, []);

  const nudgeMoving = useCallback((axis, direction) => {
    const moving = tiledImagesRef.current.moving;
    const viewer = viewerRef.current;
    if (!moving) return;

    nudgeMovingLayer(moving, axis, direction);

    if (layoutModeRef.current === 'overlay') {
      persistOverlayState();
      if (viewer) {
        viewer.world.setItemIndex(moving, viewer.world.getItemCount() - 1);
      }
    }
  }, [persistOverlayState]);

  const clearPinOverlays = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    pinOverlaysRef.current.forEach((pin) => {
      if (pin.cleanup) pin.cleanup();
      viewer.removeOverlay(pin.element);
    });
    pinOverlaysRef.current = [];
  }, []);

  const resetTwoPoint = useCallback(() => {
    twoPointRef.current = { active: false, stepIndex: 0, points: {} };
    setTwoPointActive(false);
    setTwoPointStepIndex(0);
    clearPinOverlays();
    setStatusMessage('');
    setFiducialPlacementGestures(viewerRef.current, false);
  }, [clearPinOverlays]);

  const syncPointsToFiducials = useCallback((points) => {
    twoPointRef.current.points = { ...points };
    const payload = buildFiducialsPayload(
      getImageItemId(activeDermRef.current),
      getImageItemId(activeMacroRef.current),
      points
    );
    if (payload) {
      applySavedFiducials(payload);
    }
    if (points.moveA && points.moveB && points.ontoA && points.ontoB) {
      setTwoPointStepIndex(TWO_POINT_STEPS.length);
      setStatusMessage('All four points placed. Drag pins to nudge, then click Apply.');
    }
  }, [applySavedFiducials]);

  const addPinOverlay = useCallback((viewer, tiledImage, imagePoint, colorClass, pointKey) => {
    const element = document.createElement('div');
    element.className = `coreg-pin coreg-pin--${colorClass} coreg-pin--draggable`;
    element.title = 'Drag to adjust point';
    element.setAttribute('aria-label', `Registration point ${pointKey}; drag to adjust`);

    const viewportPoint = tiledImage.imageToViewportCoordinates(imagePoint.x, imagePoint.y);
    viewer.addOverlay({
      element,
      location: viewportPoint,
      placement: OpenSeadragon.Placement.CENTER,
    });

    const pinRecord = { element, tiledImage, pointKey };
    let dragging = false;

    const onPointerDown = (e) => {
      if (layoutModeRef.current !== 'separate') return;
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      element.setPointerCapture(e.pointerId);
      element.classList.add('coreg-pin--dragging');
      const osd = viewerRef.current;
      if (osd) {
        osd.gestureSettingsMouse.dragToPan = false;
      }
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      e.preventDefault();
      e.stopPropagation();
      const osd = viewerRef.current;
      if (!osd?.canvas) return;

      const rect = osd.canvas.getBoundingClientRect();
      const pixel = new OpenSeadragon.Point(
        e.clientX - rect.left,
        e.clientY - rect.top
      );
      const nextViewport = osd.viewport.pointFromPixel(pixel);
      const imageCoords = tiledImage.viewportToImageCoordinates(nextViewport);
      const overlayLocation = tiledImage.imageToViewportCoordinates(
        imageCoords.x,
        imageCoords.y
      );

      if (typeof osd.updateOverlay === 'function') {
        osd.updateOverlay(element, overlayLocation);
      }

      twoPointRef.current.points = {
        ...twoPointRef.current.points,
        [pointKey]: { x: imageCoords.x, y: imageCoords.y },
      };
    };

    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      element.classList.remove('coreg-pin--dragging');
      try {
        element.releasePointerCapture(e.pointerId);
      } catch (_) {
        // pointer already released
      }
      const osd = viewerRef.current;
      if (osd) {
        osd.gestureSettingsMouse.dragToPan = true;
      }
      syncPointsToFiducials(twoPointRef.current.points);
    };

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', endDrag);
    element.addEventListener('pointercancel', endDrag);

    pinRecord.cleanup = () => {
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', endDrag);
      element.removeEventListener('pointercancel', endDrag);
    };

    pinOverlaysRef.current.push(pinRecord);
    return element;
  }, [syncPointsToFiducials]);

  const drawFiducialPins = useCallback((fiducials) => {
    const viewer = viewerRef.current;
    const moving = tiledImagesRef.current.moving;
    const staticImage = tiledImagesRef.current.static;
    if (!viewer || !fiducials?.points || !moving || !staticImage) return;

    clearPinOverlays();
    const { points } = fiducials;
    twoPointRef.current.points = { ...points };
    if (points.moveA) addPinOverlay(viewer, moving, points.moveA, 'move', 'moveA');
    if (points.moveB) addPinOverlay(viewer, moving, points.moveB, 'move', 'moveB');
    if (points.ontoA) addPinOverlay(viewer, staticImage, points.ontoA, 'onto', 'ontoA');
    if (points.ontoB) addPinOverlay(viewer, staticImage, points.ontoB, 'onto', 'ontoB');
  }, [addPinOverlay, clearPinOverlays]);

  const applyLayoutMode = useCallback((mode, { fitView = true } = {}) => {
    const viewer = viewerRef.current;
    const staticImage = tiledImagesRef.current.static;
    const moving = tiledImagesRef.current.moving;
    if (!staticImage || !moving) return;

    if (mode === 'separate') {
      layoutSideBySide(staticImage, moving);
      if (fiducialsRef.current && !twoPointRef.current.active) {
        drawFiducialPins(fiducialsRef.current);
      }
    } else {
      clearPinOverlays();
      applyOverlayLayout(staticImage, moving, overlayStateRef.current);
      if (viewer) {
        viewer.world.setItemIndex(moving, viewer.world.getItemCount() - 1);
      }
    }
    moving.setOpacity(dermOpacity / 100);
    layoutModeRef.current = mode;
    setLayoutMode(mode);
    if (fitView && viewer) {
      viewer.viewport.goHome(true);
    }
  }, [dermOpacity, drawFiducialPins, clearPinOverlays]);

  const buildItemMap = useCallback(() => {
    const map = new Map();
    tiledImageMetaRef.current.forEach((meta, tiledImage) => {
      if (meta.itemId) {
        map.set(meta.itemId, tiledImage);
      }
    });
    return map;
  }, []);

  const applyInitialLayout = useCallback((
    viewer,
    staticTiledImage,
    movingTiledImage,
    staticItem,
    movingItem,
    savedCoreg
  ) => {
    const staticId = getImageItemId(staticItem);
    const movingId = getImageItemId(movingItem);
    const staticSaved = savedCoreg?.images?.find(
      (img) => img.role === 'static' && img.itemId === staticId
    );
    const movingSaved = savedCoreg
      ? findCoregMovingLayer(savedCoreg, movingItem, { strict: true })
      : null;
    const pairMatches = Boolean(staticSaved && movingSaved);

    if (pairMatches) {
      applyTiledImageState(staticTiledImage, staticSaved);
      applyTiledImageState(movingTiledImage, movingSaved);
      overlayStateRef.current = captureTiledImageState(movingTiledImage);
      setDermOpacity(Math.round(movingTiledImage.opacity * 100));
      layoutModeRef.current = 'overlay';
      setLayoutMode('overlay');
      viewer.world.setItemIndex(movingTiledImage, viewer.world.getItemCount() - 1);
      setTransformMatrix(computeDermToMacroMatrix(movingTiledImage, staticTiledImage));

      if (fiducialsMatchPair(savedCoreg.fiducials, staticId, movingId)) {
        applySavedFiducials(savedCoreg.fiducials);
      } else {
        applySavedFiducials(null);
      }
      return;
    }

    overlayStateRef.current = null;
    applySavedFiducials(null);
    setTransformMatrix(null);
    layoutSideBySide(staticTiledImage, movingTiledImage);
    movingTiledImage.setOpacity(1);
    setDermOpacity(100);
    layoutModeRef.current = 'separate';
    setLayoutMode('separate');
  }, [applySavedFiducials]);

  const loadImages = useCallback(async (viewer, staticItem, movingItem) => {
    const macroUrl = createDZITileSource(staticItem);
    const dermUrl = createDZITileSource(movingItem);
    if (!macroUrl || !dermUrl) {
      throw new Error('Missing tile source for macroscopic or dermoscopic image.');
    }

    const macroId = getImageItemId(staticItem);
    const dermId = getImageItemId(movingItem);

    await new Promise((resolve, reject) => {
      viewer.addTiledImage({
        tileSource: macroUrl,
        opacity: 1,
        success: resolve,
        error: reject,
      });
    });

    const staticTiledImage = viewer.world.getItemAt(0);
    tiledImageMetaRef.current.set(staticTiledImage, {
      itemId: macroId,
      name: formatCoregImageLabel(staticItem),
      role: 'static',
    });
    tiledImagesRef.current.static = staticTiledImage;

    await new Promise((resolve, reject) => {
      viewer.addTiledImage({
        tileSource: dermUrl,
        opacity: 1,
        success: resolve,
        error: reject,
      });
    });

    const movingTiledImage = viewer.world.getItemAt(1);
    tiledImageMetaRef.current.set(movingTiledImage, {
      itemId: dermId,
      name: formatCoregImageLabel(movingItem),
      role: 'moving',
    });
    tiledImagesRef.current.moving = movingTiledImage;

    const savedCoreg = getSavedCoregState(selectedFolder);
    applyInitialLayout(
      viewer,
      staticTiledImage,
      movingTiledImage,
      staticItem,
      movingItem,
      savedCoreg
    );

    viewer.viewport.goHome(true);
  }, [selectedFolder, applyInitialLayout]);

  const swapLayerImage = useCallback(async (role, newItem) => {
    const viewer = viewerRef.current;
    if (!viewer || !newItem) return;

    const tileUrl = createDZITileSource(newItem);
    if (!tileUrl) {
      window.alert('This image cannot be loaded (missing tile ID).');
      return;
    }

    const currentStatic = activeMacroRef.current;
    const currentDerm = activeDermRef.current;
    const nextStatic = role === 'static' ? newItem : currentStatic;
    const nextDerm = role === 'moving' ? newItem : currentDerm;

    if (role === 'static' && getCoregImageKey(newItem) === getCoregImageKey(currentStatic)) return;
    if (role === 'moving' && getCoregImageKey(newItem) === getCoregImageKey(currentDerm)) return;

    setSwappingLayer(true);
    resetTwoPoint();

    try {
      const oldTiled = role === 'static'
        ? tiledImagesRef.current.static
        : tiledImagesRef.current.moving;

      if (oldTiled) {
        resetTiledImageLayer(oldTiled);
        tiledImageMetaRef.current.delete(oldTiled);
        viewer.world.removeItem(oldTiled);
      }

      const insertIndex = role === 'static' ? 0 : viewer.world.getItemCount();

      await new Promise((resolve, reject) => {
        viewer.addTiledImage({
          tileSource: tileUrl,
          index: insertIndex,
          opacity: role === 'moving' ? dermOpacity / 100 : 1,
          success: resolve,
          error: reject,
        });
      });

      const newTiled = viewer.world.getItemAt(insertIndex);
      const itemId = getImageItemId(newItem);
      tiledImageMetaRef.current.set(newTiled, {
        itemId,
        name: formatCoregImageLabel(newItem),
        role,
      });

      if (role === 'static') {
        tiledImagesRef.current.static = newTiled;
        setActiveMacro(newItem);
      } else {
        tiledImagesRef.current.moving = newTiled;
        setActiveDerm(newItem);
      }

      const staticTiledImage = tiledImagesRef.current.static;
      const movingTiledImage = tiledImagesRef.current.moving;
      if (!staticTiledImage || !movingTiledImage) return;

      const savedCoreg = getSavedCoregState(selectedFolder);
      applyInitialLayout(
        viewer,
        staticTiledImage,
        movingTiledImage,
        nextStatic,
        nextDerm,
        savedCoreg
      );
      viewer.viewport.goHome(true);
    } catch (err) {
      console.error(err);
      window.alert(err.message || 'Failed to load the selected image.');
    } finally {
      setSwappingLayer(false);
    }
  }, [applyInitialLayout, dermOpacity, resetTwoPoint, selectedFolder]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const initialMacro = macroImage ?? coregImageOptions.macroscopic[0] ?? null;
    const initialDerm = dermImage ?? coregImageOptions.dermoscopic[0] ?? null;

    setActiveMacro(initialMacro);
    setActiveDerm(initialDerm);
    activeMacroRef.current = initialMacro;
    activeDermRef.current = initialDerm;

    setLoading(true);
    setError(null);
    resetTwoPoint();
    overlayStateRef.current = null;
    fiducialsRef.current = null;
    layoutModeRef.current = 'separate';
    setLayoutMode('separate');
    setDermOpacity(100);
    setSavedFiducials(null);
    setTransformMatrix(null);
    tiledImageMetaRef.current = new Map();

    if (!initialMacro || !initialDerm) {
      setError('No macroscopic and dermoscopic images available for co-registration.');
      setLoading(false);
      return undefined;
    }

    const viewer = OpenSeadragon({
      element: viewerContainerRef.current,
      prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
      minZoomImageRatio: 0.01,
      maxZoomPixelRatio: 16,
      visibilityRatio: 0,
      // Do not set crossOriginPolicy — Girder tile JPEGs often lack CORS headers on
      // localhost, and anonymous mode blocks those loads entirely.
      showNavigator: true,
    });
    viewerRef.current = viewer;

    loadImages(viewer, initialMacro, initialDerm)
      .then(() => setLoading(false))
      .catch((err) => {
        console.error(err);
        setError(err.message || 'Failed to load images.');
        setLoading(false);
      });

    const handleCanvasClick = (event) => {
      const state = twoPointRef.current;
      if (!state.active) return;

      const step = TWO_POINT_STEPS[state.stepIndex];
      if (!step) return;

      const tiledImage =
        step.layer === 'moving'
          ? tiledImagesRef.current.moving
          : tiledImagesRef.current.static;
      if (!tiledImage) return;

      event.preventDefaultAction = true;
      const viewportPoint = viewer.viewport.pointFromPixel(event.position);
      const imagePoint = tiledImage.viewportToImageCoordinates(viewportPoint);

      state.points[step.key] = { x: imagePoint.x, y: imagePoint.y };
      addPinOverlay(viewer, tiledImage, imagePoint, step.color, step.key);

      const partial = buildFiducialsPayload(
        getImageItemId(activeDermRef.current),
        getImageItemId(activeMacroRef.current),
        state.points
      );
      if (partial) {
        applySavedFiducials(partial);
      }

      const nextIndex = state.stepIndex + 1;
      if (nextIndex >= TWO_POINT_STEPS.length) {
        state.active = false;
        setTwoPointActive(false);
        setTwoPointStepIndex(TWO_POINT_STEPS.length);
        setStatusMessage('All four points placed. Drag pins to nudge, then click Apply.');
        return;
      }

      state.stepIndex = nextIndex;
      setTwoPointStepIndex(nextIndex);
      setStatusMessage(`Click ${TWO_POINT_STEPS[nextIndex].label} on the ${TWO_POINT_STEPS[nextIndex].layer === 'moving' ? 'dermoscopic (right)' : 'macroscopic (left)'} image.`);
    };

    viewer.addHandler('canvas-click', handleCanvasClick);

    const handleResize = () => {
      const osdViewer = viewerRef.current;
      if (!osdViewer || typeof osdViewer.resize !== 'function') return;

      const element = osdViewer.element;
      if (!element || element.clientWidth <= 0 || element.clientHeight <= 0) return;

      try {
        osdViewer.resize();
      } catch (err) {
        console.warn('OpenSeadragon resize skipped in co-reg modal:', err);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      try {
        viewerRef.current?.destroy();
      } catch (err) {
        console.warn('OpenSeadragon destroy skipped in co-reg modal:', err);
      }
      viewerRef.current = null;
      tiledImagesRef.current = { static: null, moving: null };
      tiledImageMetaRef.current = new Map();
      pinOverlaysRef.current = [];
    };
    // Viewer lifecycle is tied to modal open/close only — avoid tearing down mid-session
    // when folder metadata or callbacks change (e.g. after save).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const startTwoPoint = () => {
    if (layoutMode !== 'separate') {
      applyLayoutMode('separate', { fitView: true });
    }
    resetTwoPoint();
    twoPointRef.current.active = true;
    setTwoPointActive(true);
    setFiducialPlacementGestures(viewerRef.current, true);
    setStatusMessage(`Click ${TWO_POINT_STEPS[0].label} on the dermoscopic image (right). Scroll wheel to zoom.`);
  };

  const applyTwoPoint = () => {
    const { points } = twoPointRef.current;
    const moving = tiledImagesRef.current.moving;
    const staticImage = tiledImagesRef.current.static;
    if (!moving || !staticImage) return;
    if (!points.moveA || !points.moveB || !points.ontoA || !points.ontoB) {
      window.alert('Place all four points before applying.');
      return;
    }

    try {
      const fidPayload = buildFiducialsPayload(
        getImageItemId(activeDerm),
        getImageItemId(activeMacro),
        points
      );
      applyTwoPointRegistration(moving, staticImage, points);
      const viewer = viewerRef.current;
      if (viewer) {
        viewer.world.setItemIndex(moving, viewer.world.getItemCount() - 1);
      }
      setMovingOpacity(50);
      if (fidPayload) {
        applySavedFiducials(fidPayload);
      }
      persistOverlayState();
      layoutModeRef.current = 'overlay';
      setLayoutMode('overlay');
      if (viewer) {
        viewer.viewport.goHome(true);
      }
      resetTwoPoint();
      setStatusMessage('Registration applied. Use Overlay / Separate to compare; adjust with Fine tune.');
    } catch (err) {
      window.alert(err.message);
    }
  };

  const flipMoving = () => {
    const moving = tiledImagesRef.current.moving;
    const viewer = viewerRef.current;
    if (!moving) return;
    moving.setFlip(!moving.getFlip());
    if (layoutModeRef.current === 'overlay') {
      persistOverlayState();
      if (viewer) {
        viewer.world.setItemIndex(moving, viewer.world.getItemCount() - 1);
      }
    }
  };

  const saveRegistration = async () => {
    const viewer = viewerRef.current;
    const folderKey = getCoregFolderKey(selectedFolder);
    if (!viewer || !folderKey) {
      window.alert('No folder selected.');
      return;
    }
    if (!window.confirm(
      isLoggedIn
        ? 'Save registration to this browser and the DSA folder on the server?'
        : 'Save registration in this browser? Log in from the header to also save to the DSA server.'
    )) {
      return;
    }

    let metaPatch;
    try {
      const moving = tiledImagesRef.current.moving;
      const staticImage = tiledImagesRef.current.static;
      const coregPayload = buildCoregSavePayload(
        viewer,
        tiledImageMetaRef.current,
        fiducialsRef.current,
        moving,
        staticImage
      );
      const updatedRegistrationData = updateRegistrationDataForDerm(
        selectedFolder.meta?.registrationData,
        activeDerm,
        coregPayload
      );
      metaPatch = {
        [COREG_META_KEY]: coregPayload,
        registrationData: updatedRegistrationData,
      };
    } catch (err) {
      console.error(err);
      window.alert(`Could not build registration data: ${err.message || err}`);
      return;
    }

    saveCoregToLocalStorage(folderKey, metaPatch);
    onRegistrationUpdated?.(metaPatch, { dermImage: activeDerm, macroImage: activeMacro });

    let serverSaved = false;
    let serverError = null;
    if (isLoggedIn && selectedFolder._id) {
      try {
        const serverMetadata = {
          ...(selectedFolder.meta ?? {}),
          ...metaPatch,
        };
        await ajaxActions.updateFolderMetadata(selectedFolder._id, serverMetadata);
        serverSaved = true;
      } catch (err) {
        console.error(err);
        serverError = err?.message || 'Server save failed';
      }
    }

    if (serverSaved) {
      window.alert('Registration saved to this browser and the DSA folder.');
    } else if (serverError) {
      window.alert(`Registration saved in this browser, but server save failed: ${serverError}`);
    } else {
      window.alert('Registration saved in this browser. Log in to also save to the DSA server.');
    }
  };

  const loadRegistration = async () => {
    const viewer = viewerRef.current;
    const folderKey = getCoregFolderKey(selectedFolder);
    if (!viewer || !folderKey) {
      window.alert('No folder selected.');
      return;
    }

    let coregState = loadCoregFromLocalStorage(folderKey)?.[COREG_META_KEY];
    let source = 'this browser';

    if (!coregState && isLoggedIn) {
      try {
        const folder = await ajaxActions.getFolder(selectedFolder._id);
        coregState = folder?.meta?.[COREG_META_KEY];
        if (coregState) source = 'server';
      } catch (err) {
        console.error(err);
      }
    }

    if (!coregState) {
      window.alert('No saved registration found for this folder.');
      return;
    }

    try {
      const staticImage = tiledImagesRef.current.static;
      const moving = tiledImagesRef.current.moving;
      if (!staticImage || !moving) return;

      applyInitialLayout(
        viewer,
        staticImage,
        moving,
        activeMacroRef.current,
        activeDermRef.current,
        coregState
      );
      viewer.viewport.goHome(true);
      resetTwoPoint();
      setStatusMessage(`Saved registration loaded from ${source}.`);
    } catch (err) {
      console.error(err);
      window.alert('Failed to apply saved registration.');
    }
  };

  const macroLabel = formatCoregImageLabel(activeMacro);
  const dermLabel = formatCoregImageLabel(activeDerm);
  const isBusy = loading || swappingLayer;
  const { macroscopic: macroOptions, dermoscopic: dermOptions } = coregImageOptions;

  const handleMacroSelect = (event) => {
    const key = event.target.value;
    const item = macroOptions.find((entry) => getCoregImageKey(entry) === key);
    if (item) swapLayerImage('static', item);
  };

  const handleDermSelect = (event) => {
    const key = event.target.value;
    const item = dermOptions.find((entry) => getCoregImageKey(entry) === key);
    if (item) swapLayerImage('moving', item);
  };

  return (
    <div className="coreg-modal-overlay" onClick={onClose}>
      <div className="coreg-modal-content" onClick={(e) => e.stopPropagation()}>
        <header className="coreg-modal-header">
          <div>
            <h3>Co-Register Macroscopic &amp; Dermoscopic</h3>
          </div>
          <button type="button" className="coreg-close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="coreg-viewer-wrap">
          <div ref={viewerContainerRef} className="coreg-viewer" />
          {isBusy && <div className="coreg-loading">{swappingLayer ? 'Loading image…' : 'Loading images…'}</div>}
          {error && <div className="coreg-error">{error}</div>}
        </div>

        <aside className="coreg-sidebar">
          <section className="coreg-section">
            <h4>View</h4>
            <div className="coreg-layout-toggle">
              <button
                type="button"
                className={`coreg-btn${layoutMode === 'separate' ? ' active' : ''}`}
                onClick={() => applyLayoutMode('separate')}
                disabled={isBusy}
              >
                Separate
              </button>
              <button
                type="button"
                className={`coreg-btn${layoutMode === 'overlay' ? ' active' : ''}`}
                onClick={() => applyLayoutMode('overlay')}
                disabled={isBusy}
              >
                Overlay
              </button>
            </div>
            <label className="coreg-opacity-control">
              <span className="coreg-opacity-label">Derm opacity</span>
              <input
                type="range"
                min={0}
                max={100}
                value={dermOpacity}
                disabled={isBusy}
                onChange={(e) => setMovingOpacity(Number(e.target.value))}
              />
              <span className="coreg-opacity-value">{dermOpacity}%</span>
            </label>
          </section>

          <section className="coreg-section">
            <h4>Images</h4>
            <p className="coreg-image-hint">
              Switch macroscopic or dermoscopic images for this case without closing the tool.
            </p>
            <label className="coreg-image-picker">
              <span className="coreg-image-picker-label">Reference (macroscopic)</span>
              {macroOptions.length > 1 ? (
                <select
                  className="coreg-image-select"
                  value={getCoregImageKey(activeMacro)}
                  onChange={handleMacroSelect}
                  disabled={isBusy}
                >
                  {macroOptions.map((item) => (
                    <option key={getCoregImageKey(item)} value={getCoregImageKey(item)}>
                      {formatCoregImageLabel(item)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="coreg-image-single" title={macroLabel}>{macroLabel}</span>
              )}
            </label>
            <label className="coreg-image-picker">
              <span className="coreg-image-picker-label">Moving (dermoscopic)</span>
              <div className="coreg-image-picker-row">
                {dermOptions.length > 1 ? (
                  <select
                    className="coreg-image-select"
                    value={getCoregImageKey(activeDerm)}
                    onChange={handleDermSelect}
                    disabled={isBusy}
                  >
                    {dermOptions.map((item) => (
                      <option key={getCoregImageKey(item)} value={getCoregImageKey(item)}>
                        {formatCoregImageLabel(item)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="coreg-image-single" title={dermLabel}>{dermLabel}</span>
                )}
                <button type="button" className="coreg-btn" onClick={flipMoving} disabled={isBusy}>
                  Flip
                </button>
              </div>
            </label>
          </section>

          <section className="coreg-section">
            <h4>Two-point registration</h4>
            <p>
              Pick two points on the dermoscopic image and the matching two on the macroscopic
              image (Separate view). Drag any pin afterward to nudge it. Scroll wheel zooms while placing points.
            </p>
            <div className="coreg-btn-row">
              <button
                type="button"
                className={`coreg-btn primary${twoPointActive ? ' active' : ''}`}
                onClick={startTwoPoint}
                disabled={loading || !!error}
              >
                Start
              </button>
              <button
                type="button"
                className="coreg-btn"
                onClick={applyTwoPoint}
                disabled={
                  twoPointStepIndex < TWO_POINT_STEPS.length
                  && !isCompleteFiducials(savedFiducials)
                }
              >
                Apply
              </button>
              <button type="button" className="coreg-btn" onClick={resetTwoPoint}>
                Cancel
              </button>
            </div>
            {statusMessage && (
              <div className="coreg-status">
                <strong>Status:</strong> {statusMessage}
              </div>
            )}
            {twoPointActive && twoPointStepIndex < TWO_POINT_STEPS.length && (
              <div className="coreg-status">
                Next: <strong>{TWO_POINT_STEPS[twoPointStepIndex].label}</strong> (
                {TWO_POINT_STEPS[twoPointStepIndex].layer})
              </div>
            )}
          </section>

          <section className="coreg-section">
            <h4>Fine tune</h4>
            <p>Nudge the dermoscopic layer in Overlay mode after two-point registration.</p>
            <div className="coreg-nudge-grid">
              <div className="coreg-nudge-row">
                <span className="coreg-nudge-label">X</span>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('x', -1)} aria-label="Nudge left">−</button>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('x', 1)} aria-label="Nudge right">+</button>
              </div>
              <div className="coreg-nudge-row">
                <span className="coreg-nudge-label">Y</span>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('y', -1)} aria-label="Nudge up">−</button>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('y', 1)} aria-label="Nudge down">+</button>
              </div>
              <div className="coreg-nudge-row">
                <span className="coreg-nudge-label">Scale</span>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('scale', -1)} aria-label="Scale down">−</button>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('scale', 1)} aria-label="Scale up">+</button>
              </div>
              <div className="coreg-nudge-row">
                <span className="coreg-nudge-label">Angle</span>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('angle', -1)} aria-label="Rotate counter-clockwise">−</button>
                <button type="button" className="coreg-nudge-btn" disabled={isBusy} onClick={() => nudgeMoving('angle', 1)} aria-label="Rotate clockwise">+</button>
              </div>
            </div>
          </section>

          <section className="coreg-section">
            <h4>Registration state</h4>
            <p>
              Always saves to this browser. When logged in, also writes XFM / co-registration
              data to the DSA folder metadata and updates dermoscopic{' '}
              <code>registrationData</code>.
            </p>
            <p className={`coreg-auth-status${isLoggedIn ? ' coreg-auth-status--logged-in' : ''}`}>
              {isLoggedIn
                ? 'Logged in — server save is enabled.'
                : 'Not logged in — use Log in in the header to save to the server.'}
            </p>
            <div className="coreg-btn-row">
              <button type="button" className="coreg-btn" onClick={loadRegistration} disabled={isBusy}>
                Load
              </button>
              <button type="button" className="coreg-btn primary" onClick={saveRegistration} disabled={isBusy}>
                Save
              </button>
            </div>
          </section>

          {savedFiducials?.points && (
            <section className="coreg-section">
              <h4>Fiducial points</h4>
              <p>Saved in image pixel coordinates. Shown on images in Separate view.</p>
              <table className="coreg-data-table">
                <thead>
                  <tr>
                    <th>Point</th>
                    <th>Image</th>
                    <th>X, Y (px)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'moveA', label: 'A', image: 'Derm' },
                    { key: 'moveB', label: 'B', image: 'Derm' },
                    { key: 'ontoA', label: 'A', image: 'Macro' },
                    { key: 'ontoB', label: 'B', image: 'Macro' },
                  ].map(({ key, label, image }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>{image}</td>
                      <td className="coreg-data-mono">
                        {formatFiducialPoint(savedFiducials.points[key])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="coreg-btn"
                disabled={loading || layoutMode !== 'separate'}
                onClick={() => drawFiducialPins(savedFiducials)}
              >
                Show on images
              </button>
            </section>
          )}

          {transformMatrix && (
            <section className="coreg-section">
              <h4>Transform matrix</h4>
              <p className="coreg-matrix-caption">{transformMatrix.label}</p>
              <table className="coreg-matrix">
                <tbody>
                  {transformMatrix.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, colIndex) => (
                        <td key={colIndex}>{formatMatrixCell(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

export default CoregModal;
